import React from 'react';
import studioImage from '../images/about/ram-tirath-art-studio.jpg';

const ArtistProfile = () => {
  const leadershipTeam = [
    {
      name: 'Dr. Ram Tirath Patel',
      role: 'Artist',
      hindiTag: 'मूर्तिकार',
      phone: '+91 9450686765',
      phoneRaw: '9450686765',
      description: 'Master portrait sculptor & creative director specializing in Life and Portrait sculptures, custom bust statues, and large-scale art projects.'
    },
    {
      name: 'Sandeep Patel',
      role: 'Managing Director & Studio Operations',
      phone: '+91 9161410260',
      phoneRaw: '9161410260',
      description: 'Leading studio manufacturing, material selection, fiberglass and metal execution, and structural installation logistics across India.'
    },
    {
      name: 'Satyanam Patel',
      role: 'Managing Director & Client Relations',
      phone: '+91 8887732619',
      phoneRaw: '8887732619',
      description: 'Handling client consultations, custom sculpture commissions, quality standards, and dedicated customer support.'
    }
  ];

  const academicQualifications = [
    { degree: 'Bombay Art', year: '2004', institute: 'Bombay Art' },
    { degree: 'B.F.A.', year: '2005 – 2008', institute: 'Lucknow University' },
    { degree: 'M.F.A.', year: '2009 – 2010', institute: 'Lucknow University' },
    { degree: 'U.G.C. NET', year: 'June 2012', institute: 'National Eligibility Test' },
    { degree: 'D.El.Ed', year: '2019', institute: 'Diploma in Elementary Education' },
    { degree: 'Ph.D', year: '2026', institute: 'JJTU University' },
  ];

  return (
    <section id="about" className="py-16 md:py-24 bg-gradient-to-b from-gray-50 via-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Main Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
            About <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-600">Ram Tirath Art</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Crafting timeless handmade sculptures, custom life-size statues, and 3D murals with passion, precision, and academic heritage.
          </p>
          <div className="w-24 h-1.5 bg-gradient-to-r from-orange-500 to-amber-500 mx-auto mt-6 rounded-full"></div>
        </div>

        {/* 1. Leadership & Co-Founders Team Cards Section */}
        <div className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {leadershipTeam.map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col justify-between group hover:-translate-y-1"
              >
                <div>
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 text-white rounded-2xl flex items-center justify-center font-bold text-xl mb-6 shadow-lg shadow-orange-500/20 group-hover:scale-110 transition-transform duration-300">
                    {member.name.includes('Dr.') ? 'R' : member.name.charAt(0)}
                  </div>

                  {member.hindiTag && (
                    <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2.5 py-1 rounded-full uppercase tracking-wider">
                      {member.hindiTag}
                    </span>
                  )}

                  <h4 className="text-xl font-bold text-gray-900 mt-2 mb-1">
                    {member.name}
                  </h4>
                  <p className="text-sm font-semibold text-orange-600 mb-4">
                    {member.role}
                  </p>

                  <p className="text-sm text-gray-600 leading-relaxed mb-6">
                    {member.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-100 mt-auto">
                  <a
                    href={`tel:${member.phoneRaw}`}
                    className="inline-flex items-center justify-center w-full bg-gray-900 hover:bg-orange-600 text-white text-sm font-semibold py-2.5 px-4 rounded-xl transition-colors duration-200 shadow-sm gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Call {member.phone}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 2. Main Founder Spotlight & Story Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-100 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center mb-8">
              <div className="md:col-span-5 overflow-hidden rounded-2xl shadow-md border border-gray-100">
                <img
                  src={studioImage}
                  alt="Dr. Ram Tirath Patel - Ram Tirath Art Studio"
                  className="w-full h-64 md:h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="md:col-span-7">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                  About Dr. Ram Tirath Patel
                </h3>
                <div className="space-y-4 text-gray-600 leading-relaxed text-base md:text-lg">
                  <p>
                    <strong className="text-gray-900">Dr. Ram Tirath Patel</strong> specialized in <strong>'Life and Portrait'</strong> sculpture at the College of Arts and Crafts, Lucknow University, and established <strong className="text-gray-900 font-semibold">Ram Tirath Art</strong> alongside his brothers.
                  </p>
                  <p className="text-gray-700 font-medium">
                    Today, Ram Tirath Art leads a team of <strong>over 25+ skilled craftsmen</strong> executing custom handmade fiberglass, metal, and stone sculptures across India.
                  </p>
                </div>
              </div>
            </div>

            {/* Academic Background & Qualifications Grid */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200/80 mb-8">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>🎓</span> Academic & Professional Qualifications
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {academicQualifications.map((item, index) => (
                  <div key={index} className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
                    <span className="text-sm font-bold text-orange-600">{item.degree}</span>
                    <span className="text-xs text-gray-700 font-medium mt-0.5">{item.institute}</span>
                    <span className="text-[11px] text-gray-400 font-semibold mt-1">{item.year}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quote Block */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-6 border-l-4 border-orange-500 shadow-sm">
              <p className="text-orange-950 font-medium italic text-base md:text-xl leading-relaxed">
                "At Ram Tirath Art, art is more than decoration — it is a timeless story carved with passion, precision, and purpose."
              </p>
              <span className="block mt-3 text-xs font-bold uppercase tracking-wider text-orange-700">
                — Dr. Ram Tirath Patel, Sculpture Artist
              </span>
            </div>
          </div>
        </div>

        {/* 3. Stats Section */}
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white rounded-3xl p-8 sm:p-12 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-8 -mr-8 w-48 h-48 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center relative z-10">
            <div>
              <div className="text-3xl sm:text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400 mb-2">
                15+
              </div>
              <div className="text-xs sm:text-sm uppercase tracking-wider text-gray-300 font-semibold">
                Years of Excellence
              </div>
              <div className="text-xs text-gray-400 mt-1">Est. 2004</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400 mb-2">
                1000+
              </div>
              <div className="text-xs sm:text-sm uppercase tracking-wider text-gray-300 font-semibold">
                Sculptures Created
              </div>
              <div className="text-xs text-gray-400 mt-1">Statues & Murals</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400 mb-2">
                25+
              </div>
              <div className="text-xs sm:text-sm uppercase tracking-wider text-gray-300 font-semibold">
                Master Artisans
              </div>
              <div className="text-xs text-gray-400 mt-1">Skilled Studio Crew</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400 mb-2">
                100%
              </div>
              <div className="text-xs sm:text-sm uppercase tracking-wider text-gray-300 font-semibold">
                Handmade Craft
              </div>
              <div className="text-xs text-gray-400 mt-1">Fiberglass & Metal</div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ArtistProfile;
